import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { GetAccess, GetRole } from "../../../../Components/Utils/roles";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import { PremiumTable, CopyButton } from "../../../../Components/Others/PremiumTable";
import NuAmount from "../../../../Components/Utils/NuAmount";
import axiosInstance from "../../../../Services/axiosCommon";
import { useToast } from "../../../../Components/Others/ToastServices";

// Helper for downloading PDF
export const downloadProformaPdf = async (id, piNumber = "Proforma-Invoice", toast = null) => {
  try {
    const res = await axiosInstance.get(`/proforma-invoice/download-pdf/${id}`, {
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${piNumber || "Proforma-Invoice"}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    if (toast) {
      toast.open({
        type: "success",
        message: "Download Started",
        description: `Downloading ${piNumber}.pdf`,
      });
    }
  } catch (err) {
    console.error("PDF download failed:", err);
    if (toast) {
      toast.open({
        type: "error",
        message: "Download Failed",
        description: "Could not generate or download PDF.",
      });
    }
  }
};

// Proforma Invoice Table Row Component
const ProformaInvoiceTableRow = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
}) => {
  const toast = useToast();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("proforma_invoice_edit") || GetRole();
  const canDelete = GetAccess("proforma_invoice_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/proforma-invoice/${row.id}`, setDeleteConfirmOpen);
  };

  const handleDownload = async () => {
    setDownloading(true);
    await downloadProformaPdf(row.id, row.piNumber || row.ref || `PI-${row.id}`, toast);
    setDownloading(false);
  };

  const piRef = row.piNumber || row.ref || `PI-${row.id || "N/A"}`;
  const totalAmount = row.totalAmount ?? row.grandTotal ?? row.totalPrice ?? row.subTotal ?? 0;

  return (
    <>
      <tr className="bg-white hover:bg-white/80 transition-colors align-middle">
        {/* PI Number */}
        <td className="p-4 align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl">
          <div className="flex items-center text-xs 2xl:text-sm font-semibold text-gray-900">
            <Link
              to={`/sales/proforma-invoice/view/${row.id}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="hover:underline text-gray-900 hover:text-emerald-600 transition-colors font-bold"
            >
              {piRef}
            </Link>
            <CopyButton text={piRef} />
          </div>
        </td>

        {/* Customer */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs 2xl:text-sm text-gray-800 font-medium">
            {row.customerName || row.customer?.companyName || row.customer?.name || "N/A"}
          </span>
        </td>

        {/* Date */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs 2xl:text-sm text-gray-600 font-medium">
            {row.date ? moment(row.date).format("DD-MMM-YYYY") : "N/A"}
          </span>
        </td>

        {/* Bank Name */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs 2xl:text-sm text-gray-800 font-medium">
            {row.bankName || row.bank?.bankName || "N/A"}
          </span>
        </td>

        {/* Account Name */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs 2xl:text-sm text-gray-800 font-medium">
            {row.accountName || row.bank?.accountName || "N/A"}
          </span>
        </td>

        {/* Total Amount */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs 2xl:text-sm font-bold text-gray-900">
            <NuAmount currency={row.currencyName || row.currency} value={totalAmount} />
          </span>
        </td>

        {/* Actions */}
        <td className="p-4 text-center align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
          <div className="flex items-center justify-center gap-1.5">
            {/* View */}
            <Link
              to={`/sales/proforma-invoice/view/${row.id}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
              title="View details"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>

            {/* Download PDF */}
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="border border-emerald-200 text-emerald-600 hover:bg-emerald-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer disabled:opacity-50"
              title="Download PDF"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>

            {/* Edit */}
            {canEdit && (
              <Link
                to={`/sales/proforma-invoice/edit/${row.id}`}
                state={{ data: row, from: "/sales/proforma-invoice", pageIndex, searchQuery }}
                className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Edit PI"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            )}

            {/* Delete */}
            {canDelete && (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer"
                title="Delete PI"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Delete Proforma Invoice"
          content={`Are you sure you want to delete "${piRef}"? This will cascade-delete all mapped product line items.`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </>
  );
};

// Proforma Invoice Card Component for Mobile / Grid
const ProformaInvoiceCard = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
}) => {
  const toast = useToast();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("proforma_invoice_edit") || GetRole();
  const canDelete = GetAccess("proforma_invoice_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/proforma-invoice/${row.id}`, setDeleteConfirmOpen);
  };

  const handleDownload = async () => {
    setDownloading(true);
    await downloadProformaPdf(row.id, row.piNumber || row.ref || `PI-${row.id}`, toast);
    setDownloading(false);
  };

  const piRef = row.piNumber || row.ref || `PI-${row.id || "N/A"}`;
  const totalAmount = row.totalAmount ?? row.grandTotal ?? row.totalPrice ?? row.subTotal ?? 0;

  return (
    <div className="bg-white border border-gray-200/80 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between">
      <div>
        <div className="flex items-start justify-between gap-2 pb-3 border-b border-gray-100">
          <div>
            <Link
              to={`/sales/proforma-invoice/view/${row.id}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="text-sm font-bold text-gray-900 hover:text-emerald-600 transition-colors"
            >
              {piRef}
            </Link>
            <p className="text-xs text-gray-500 font-medium mt-0.5">
              {row.customerName || row.customer?.companyName || row.customer?.name || "N/A"}
            </p>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
            {row.currencyName || row.currency || "SAR"}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2.5 py-3 text-xs">
          <div>
            <span className="text-gray-400 block text-[11px]">Date</span>
            <span className="font-medium text-gray-700 mt-0.5 block">
              {row.date ? moment(row.date).format("DD-MMM-YYYY") : "N/A"}
            </span>
          </div>

          <div>
            <span className="text-gray-400 block text-[11px]">Bank Name</span>
            <span className="font-medium text-gray-700 mt-0.5 block truncate">
              {row.bankName || row.bank?.bankName || "N/A"}
            </span>
          </div>

          <div>
            <span className="text-gray-400 block text-[11px]">Account Name</span>
            <span className="font-medium text-gray-700 mt-0.5 block truncate">
              {row.accountName || row.bank?.accountName || "N/A"}
            </span>
          </div>

          <div>
            <span className="text-gray-400 block text-[11px]">Total Amount</span>
            <span className="text-sm font-bold text-gray-900 mt-0.5 block">
              <NuAmount currency={row.currencyName || row.currency} value={totalAmount} />
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-1.5 pt-3 border-t border-gray-100">
        <Link
          to={`/sales/proforma-invoice/view/${row.id}`}
          state={{ data: row, pageIndex, searchQuery }}
          className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
          title="View details"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </Link>

        <button
          onClick={handleDownload}
          disabled={downloading}
          className="border border-emerald-200 text-emerald-600 hover:bg-emerald-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer disabled:opacity-50"
          title="Download PDF"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>

        {canEdit && (
          <Link
            to={`/sales/proforma-invoice/edit/${row.id}`}
            state={{ data: row, from: "/sales/proforma-invoice", pageIndex, searchQuery }}
            className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
            title="Edit PI"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </Link>
        )}

        {canDelete && (
          <button
            onClick={() => setDeleteConfirmOpen(true)}
            className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer"
            title="Delete PI"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Delete Proforma Invoice"
          content={`Are you sure you want to delete "${piRef}"?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// Main ProformaInvoiceTable Component
const ProformaInvoiceTable = ({
  data = [],
  total = 0,
  pageIndex,
  setPageIndex,
  dataCount,
  setDataCount,
  searchQuery,
  fetcher,
  dataLoading = false,
  viewMode = "table",
}) => {
  const headers = [
    { title: "PI No." },
    { title: "Customer" },
    { title: "Date" },
    { title: "Bank Name" },
    { title: "Account Name" },
    { title: "Total Amount" },
    { title: "Actions", align: "center" },
  ];

  return (
    <PremiumTable
      data={data}
      total={total}
      pageIndex={pageIndex}
      setPageIndex={setPageIndex}
      dataCount={dataCount}
      setDataCount={setDataCount}
      viewMode={viewMode}
      headers={headers}
      renderRow={(row, idx) => (
        <ProformaInvoiceTableRow
          key={row.id || idx}
          row={row}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
        />
      )}
      renderCard={(row, idx) => (
        <ProformaInvoiceCard
          key={row.id || idx}
          row={row}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
        />
      )}
      dataLoading={dataLoading}
    />
  );
};

export default ProformaInvoiceTable;
